/**
 * File element renderer (F-135) — registries/element-types/file per docs/08
 * §2: one folder per element type; the canvas layer knows nothing about it.
 * Selection / drag / resize / z-order / delete are the SHARED element
 * machinery (ElementsLayer's ElementView); only the card's interior and the
 * download action (./download.ts, ./toolbar.tsx) are file-specific.
 *
 * Three faces, from the payload's `media` — the SERVER's verdict at upload:
 *  - video: the name strip over an inline `<video controls>`;
 *  - audio: the name strip over an `<audio controls>` row;
 *  - file: the name strip over the extension in large type and a hint — a
 *    PDF, a project file, a video the browser cannot play. A player whose
 *    bytes turn out unplayable after all (`onError`) falls back to this face,
 *    so nobody is left staring at a dead control.
 *
 * Pointer events: the card is inert (`pointer-events: none`, app.css) so the
 * `.board-element` wrapper owns select and drag, exactly like an image or a
 * link card. The PLAYER re-enables events only while the element is
 * SELECTED (`.board-element.selected .file-card-media`), and swallows its
 * pointerdown so a click on the scrubber never becomes a drag — the first
 * click selects the card, the next ones drive the player, and the name strip
 * stays a drag handle throughout.
 */
import { isFileElementPayload, type FileMedia } from "@kola/shared";
import { useState, type PointerEvent } from "react";

import { assetUrl } from "../../../core/api.js";
import { fileExtension, formatBytes } from "../../../features/elements/file-kind.js";
import { svgProps } from "../../../ui/icons.js";
import type { ElementRendererProps } from "../image/ImageElement.js";

const iconVideo = (
  <svg {...svgProps} aria-hidden="true">
    <rect x="2.5" y="4" width="8" height="8" rx="1" />
    <path d="m10.5 7 3-1.8v5.6l-3-1.8" />
  </svg>
);
const iconAudio = (
  <svg {...svgProps} aria-hidden="true">
    <path d="M6 10.5V4.5l6-1.5v6" />
    <circle cx="4.5" cy="10.5" r="1.5" />
    <circle cx="10.5" cy="9" r="1.5" />
  </svg>
);
const iconFile = (
  <svg {...svgProps} aria-hidden="true">
    <path d="M4 2.5h5l3 3v8a.9.9 0 0 1-.9.9H4a.9.9 0 0 1-.9-.9V3.4a.9.9 0 0 1 .9-.9z" />
    <path d="M9 2.5v3h3" />
  </svg>
);

function iconFor(media: FileMedia) {
  return media === "video" ? iconVideo : media === "audio" ? iconAudio : iconFile;
}

/** A click on the player is for the player, not the start of an element drag. */
function keepForPlayer(event: PointerEvent) {
  event.stopPropagation();
}

export function FileElementView({ element }: ElementRendererProps) {
  const payload = element.payload;
  // Hooks before the early return (Rules of Hooks); a malformed payload —
  // impossible from our writers, possible from a future one — renders nothing.
  const [unplayable, setUnplayable] = useState(false);
  if (!isFileElementPayload(payload)) return null;

  const header = (
    <div className="file-card-head">
      <span className="file-card-icon">{iconFor(payload.media)}</span>
      <span className="file-card-name" title={payload.name}>
        {payload.name}
      </span>
      <span className="file-card-size">{formatBytes(payload.size)}</span>
    </div>
  );

  if (payload.media === "video" && !unplayable) {
    return (
      <div className="file-card file-card-video">
        {header}
        <video
          className="file-card-media"
          src={assetUrl(payload.assetId)}
          controls
          preload="metadata"
          playsInline
          onPointerDown={keepForPlayer}
          onError={() => setUnplayable(true)}
        />
      </div>
    );
  }

  if (payload.media === "audio" && !unplayable) {
    return (
      <div className="file-card file-card-audio">
        {header}
        <audio
          className="file-card-media"
          src={assetUrl(payload.assetId)}
          controls
          preload="metadata"
          onPointerDown={keepForPlayer}
          onError={() => setUnplayable(true)}
        />
      </div>
    );
  }

  const ext = fileExtension(payload.name);
  return (
    <div className="file-card file-card-generic">
      {header}
      <div className="file-card-body">
        <span className="file-card-ext">{ext === "" ? "FILE" : ext.toUpperCase()}</span>
        <span className="file-card-hint">
          {unplayable ? "Can't play here — download to open" : "Select it, then ↓ Download"}
        </span>
      </div>
    </div>
  );
}
